import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import {
  IWebsiteBuilderDividerSettings,
  TWebsiteBuilderDividerStyle,
  WebsiteBuilderDividerWeight,
} from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderDivider";
import { Select } from "components/select/index";
import { useAppDispatch } from "hooks/redux";
import { websiteBuilderUpdateElementSettings } from "redux/actions/websiteBuilder/websiteBuilder";
import {
  IWebsiteBuilderPopupOption,
  renderWebsiteBuilderPopupOptions,
} from "../../../../helpers/popup/renderWebsiteBuilderPopupOptions";
import { WebsiteBuilderPopupSections } from "../../../../helpers/popup/WebsiteBuilderPopupSections";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";

interface IDividerSettingsProps {
  id: string;
  settings: IWebsiteBuilderDividerSettings;
}

export function DividerSettings(props: IDividerSettingsProps) {
  const { id, settings } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [updatedSettings, setUpdatedSettings] = useState<IWebsiteBuilderDividerSettings>(cloneDeep(settings));

  const weightOptions: IWebsiteBuilderPopupOption<WebsiteBuilderDividerWeight>[] = useMemo(() => {
    return [
      {
        label: t("Thin"),
        value: WebsiteBuilderDividerWeight.THIN,
      },
      {
        label: t("Regular"),
        value: WebsiteBuilderDividerWeight.REGULAR,
      },
      {
        label: t("Thick"),
        value: WebsiteBuilderDividerWeight.THICK,
      },
      {
        label: t("Extra Thick"),
        value: WebsiteBuilderDividerWeight.EXTRA_THICK,
      },
    ];
  }, []);

  const styleOptions: IWebsiteBuilderPopupOption<TWebsiteBuilderDividerStyle>[] = useMemo(() => {
    return [
      {
        label: t("Solid"),
        value: "solid",
      },
      {
        label: t("Dashed"),
        value: "dashed",
      },
      {
        label: t("Dotted"),
        value: "dotted",
      },
    ];
  }, []);

  function dispatchSettings(
    prevSettings: IWebsiteBuilderDividerSettings,
    nextSettings: IWebsiteBuilderDividerSettings,
  ) {
    dispatch(
      websiteBuilderUpdateElementSettings({
        id,
        prevElementSettings: {
          type: "divider",
          settings: cloneDeep(prevSettings),
        },
        nextElementSettings: {
          type: "divider",
          settings: cloneDeep(nextSettings),
        },
      }),
    );
  }

  const { handleUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: settings,
    updatedState: updatedSettings,
    setUpdatedState: setUpdatedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function renderSettings() {
    return (
      <>
        <Select
          label={t("Weight")}
          onChange={(weight: WebsiteBuilderDividerWeight) =>
            handleUpdateState(prev => ({
              ...prev,
              weight,
            }))
          }
          defaultValue={updatedSettings.weight}
        >
          {renderWebsiteBuilderPopupOptions(weightOptions)}
        </Select>
        <Select
          label={t("Style")}
          onChange={(style: TWebsiteBuilderDividerStyle) =>
            handleUpdateState(prev => ({
              ...prev,
              style,
            }))
          }
          defaultValue={updatedSettings.style}
        >
          {renderWebsiteBuilderPopupOptions(styleOptions)}
        </Select>
      </>
    );
  }

  return <WebsiteBuilderPopupSections sections={[renderSettings()]} />;
}
